// JobSheetView.jsx - the internal job sheet on screen, in the same tab: a bar
// with a way back and a Print button over the sheet itself (an iframe, so its
// print layout in jobSheet.js is exactly what prints). Used by the
// /jobsheet/:quoteId page (from the Quote Log) and as a full-screen overlay
// from the calculators, where leaving the page would lose unsaved inputs.
import React, { useState, useEffect, useRef } from "react";
import { fetchAllQuotes } from "./quoteLogger";
import { buildJobSheetHTMLFromQuote } from "./jobSheetFromQuote";

const barBtn = { color: "#fff", background: "transparent", fontSize: 13, fontWeight: 700, textDecoration: "none", padding: "8px 12px", borderRadius: 6, border: "1px solid #2a3b55", cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap" };

// onHome (overlay): the logo's way to the Quote Log; the page uses backHref.
export function JobSheetView({ html, backLabel, backHref, onBack, onHome, overlay }) {
  const iframeRef = useRef(null);
  const handlePrint = () => {
    const w = iframeRef.current && iframeRef.current.contentWindow;
    if (w) { w.focus(); w.print(); }
  };
  return (
    <div style={overlay ? { position: "fixed", inset: 0, zIndex: 1000, background: "#eef1f5", display: "flex", flexDirection: "column" } : { display: "flex", flexDirection: "column", height: "100vh", background: "#eef1f5" }}>
      <div style={{ background: "#0e1520", borderBottom: "1px solid #1e2d42", padding: "calc(10px + env(safe-area-inset-top, 0px)) 20px 10px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, fontFamily: "'IBM Plex Sans', sans-serif" }}>
        {/* Phones: Back and Print on one line; the logo and label are dropped (Back does the logo's job). */}
        <style>{`@media (max-width: 600px) { .jobsheet-label, .jobsheet-logo { display: none !important; } }`}</style>
        <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0 }}>
          {/* Logo: the Quote Log, in this tab. */}
          {backHref
            ? <a className="jobsheet-logo" href={backHref} onClick={onBack} aria-label="Sublime Precision Cleaning: Quote Log" style={{ display: "inline-flex", lineHeight: 0 }}><img src="/logo.png" alt="Sublime Precision Cleaning" style={{ height: 26, width: "auto" }} /></a>
            : onHome && <button className="jobsheet-logo" type="button" onClick={onHome} aria-label="Sublime Precision Cleaning: Quote Log" style={{ display: "inline-flex", lineHeight: 0, background: "none", border: "none", padding: 0, cursor: "pointer" }}><img src="/logo.png" alt="Sublime Precision Cleaning" style={{ height: 26, width: "auto" }} /></button>}
          {backHref
            ? <a href={backHref} onClick={onBack} style={barBtn}>{backLabel}</a>
            : <button type="button" onClick={onBack} style={barBtn}>{backLabel}</button>}
          <span className="jobsheet-label" style={{ color: "#7a8fa8", fontSize: 12, whiteSpace: "nowrap" }}>Job Sheet · internal</span>
        </div>
        <button type="button" onClick={handlePrint} style={{ background: "#3a52cc", color: "#fff", border: "none", padding: "10px 20px", borderRadius: 6, fontWeight: 700, fontSize: 12, cursor: "pointer", letterSpacing: "0.05em", whiteSpace: "nowrap" }}>
          Print
        </button>
      </div>
      <iframe ref={iframeRef} srcDoc={html} title="Job Sheet" style={{ flex: 1, width: "100%", border: "none", display: "block" }} />
    </div>
  );
}

// /jobsheet/:quoteId - signed-in only (see index.js); opened from the Quote Log.
export default function JobSheetPage({ quoteId }) {
  const [html, setHtml] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchAllQuotes()
      .then(quotes => {
        const quote = quotes.find(q => q["Quote Number"] === quoteId);
        if (!quote) throw new Error("Quote not found.");
        setHtml(buildJobSheetHTMLFromQuote(quote));
      })
      .catch(e => setError(e.message));
  }, [quoteId]);

  const backHref = `/?open=${encodeURIComponent(quoteId)}`;
  const onBack = () => { try { window.sessionStorage.setItem("sublime_active_tab", "log"); } catch (e) { /* storage blocked */ } };

  if (error || !html) return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16, alignItems: "center", justifyContent: "center", height: "100vh", fontFamily: "'IBM Plex Sans', sans-serif", fontSize: 16, color: error ? "#E53935" : "#1D3461" }}>
      {error || "Loading job sheet..."}
      {error && <a href={backHref} onClick={onBack} style={{ fontSize: 14, color: "#3a52cc" }}>← Back to Quote Log</a>}
    </div>
  );
  return <JobSheetView html={html} backLabel="← Back to Quote Log" backHref={backHref} onBack={onBack} />;
}
